import { useMemo } from 'react';
import { useStore, type TableFilter } from '../store';

// Header と Tab の間に出すサマリ帯。重大・警告・情報 を一目で把握できるようにし、
// クリックで該当タブへ遷移する。問題が一切無いときは控えめな "クリーン" 表示にする。
//
// 個別シグナル（alt 欠 / h1 異常 / canonical 壊 / title 欠）は TableView の "詳細フィルタ" を
// 兼ねており、クリックでテーブルタブに切り替えつつそのフィルタを適用する（連動）
export function WarningsSummary() {
  const data = useStore((s) => s.data);
  const setActiveTab = useStore((s) => s.setActiveTab);
  const setTableFilter = useStore((s) => s.setTableFilter);
  const tableFilter = useStore((s) => s.tableFilter);
  const activeTab = useStore((s) => s.activeTab);

  const counts = useMemo(() => {
    if (!data) return null;
    let critical = 0;
    let warning = 0;
    let info = 0;
    for (const w of data.warnings) {
      if (w.severity === 'critical') critical++;
      else if (w.severity === 'high') critical++;
      else if (w.severity === 'medium') warning++;
      else info++;
    }
    let altMissing = 0;
    let h1Multiple = 0;
    let canonicalBroken = 0;
    let titleMissing = 0;
    for (const p of data.pages) {
      altMissing +=
        p.images?.filter((img) => img.alt === undefined || img.alt === '').length ?? 0;
      if ((p.headings?.h1.length ?? 1) !== 1) h1Multiple++;
      if (p.canonicalCheck && p.canonicalCheck.status >= 400) canonicalBroken++;
      if (!p.title) titleMissing++;
    }
    return { critical, warning, info, altMissing, h1Multiple, canonicalBroken, titleMissing };
  }, [data]);

  if (!data || !counts) return null;

  const totalWarnings = counts.critical + counts.warning + counts.info;
  const hasIssue =
    totalWarnings > 0 ||
    counts.altMissing > 0 ||
    counts.h1Multiple > 0 ||
    counts.canonicalBroken > 0 ||
    counts.titleMissing > 0;

  if (!hasIssue) {
    return (
      <div className="border-b border-emerald-200 bg-emerald-50">
        <div className="mx-auto max-w-7xl px-6 py-2 text-xs text-emerald-700">
          ✓ 主要なチェック項目で問題は検出されませんでした
        </div>
      </div>
    );
  }

  // テーブルフィルタを適用しつつテーブルタブへ
  const applyTableFilter = (f: TableFilter): void => {
    setTableFilter(f);
    setActiveTab('table');
  };

  // テーブルタブ表示中に、現在の tableFilter とこのチップの filter が一致するか
  const isTableFilterActive = (f: TableFilter): boolean => {
    if (activeTab !== 'table') return false;
    if (tableFilter.kind !== f.kind) return false;
    if (tableFilter.kind === 'warning' && f.kind === 'warning') {
      return tableFilter.warning === f.warning;
    }
    return false;
  };

  return (
    <div className="border-b border-slate-200 bg-slate-50">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-2 px-6 py-2">
        <span className="mr-1 text-[11px] font-medium uppercase tracking-wide text-slate-500">
          要確認
        </span>
        {counts.critical > 0 && (
          <SummaryPill
            tone="red"
            label="重大"
            count={counts.critical}
            onClick={() => setActiveTab('warnings')}
            title="エラー / 5xx / リダイレクトループ / 壊れたリンク（警告タブで詳細を確認）"
          />
        )}
        {counts.warning > 0 && (
          <SummaryPill
            tone="amber"
            label="警告"
            count={counts.warning}
            onClick={() => setActiveTab('warnings')}
            title="重複 canonical / 遅いページ など（警告タブで詳細を確認）"
          />
        )}
        {counts.info > 0 && (
          <SummaryPill
            tone="slate"
            label="情報"
            count={counts.info}
            onClick={() => setActiveTab('warnings')}
            title="title 欠 / h1 欠 など（警告タブで詳細を確認）"
          />
        )}

        {(counts.altMissing > 0 ||
          counts.h1Multiple > 0 ||
          counts.canonicalBroken > 0 ||
          counts.titleMissing > 0) && (
          <span className="mx-1 hidden h-4 w-px bg-slate-300 sm:inline-block" />
        )}

        {counts.altMissing > 0 && (
          <SummaryPill
            tone="amber"
            label="alt 欠"
            count={counts.altMissing}
            onClick={() => applyTableFilter({ kind: 'warning', warning: 'altMissing' })}
            active={isTableFilterActive({ kind: 'warning', warning: 'altMissing' })}
            title="alt 属性なし or 空文字の画像枚数（クリックでテーブルを該当ページのみに絞り込み）"
          />
        )}
        {counts.h1Multiple > 0 && (
          <SummaryPill
            tone="amber"
            label="h1 異常"
            count={counts.h1Multiple}
            onClick={() => applyTableFilter({ kind: 'warning', warning: 'h1Abnormal' })}
            active={isTableFilterActive({ kind: 'warning', warning: 'h1Abnormal' })}
            title="h1 が 0 または複数のページ数（クリックでテーブル絞り込み）"
          />
        )}
        {counts.canonicalBroken > 0 && (
          <SummaryPill
            tone="red"
            label="canonical 壊"
            count={counts.canonicalBroken}
            onClick={() => applyTableFilter({ kind: 'warning', warning: 'canonicalBroken' })}
            active={isTableFilterActive({ kind: 'warning', warning: 'canonicalBroken' })}
            title="canonical 先が 4xx 以上のページ数（クリックでテーブル絞り込み）"
          />
        )}
        {counts.titleMissing > 0 && (
          <SummaryPill
            tone="amber"
            label="title 欠"
            count={counts.titleMissing}
            onClick={() => applyTableFilter({ kind: 'warning', warning: 'titleMissing' })}
            active={isTableFilterActive({ kind: 'warning', warning: 'titleMissing' })}
            title="title 未設定のページ数（クリックでテーブル絞り込み）"
          />
        )}
      </div>
    </div>
  );
}

function SummaryPill({
  tone,
  label,
  count,
  onClick,
  title,
  active = false,
}: {
  tone: 'red' | 'amber' | 'slate';
  label: string;
  count: number;
  onClick: () => void;
  title?: string;
  active?: boolean;
}) {
  const inactiveColors = {
    red: 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100',
    amber: 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100',
    slate: 'border-slate-200 bg-white text-slate-700 hover:bg-slate-100',
  }[tone];
  const activeColors = {
    red: 'border-red-600 bg-red-600 text-white',
    amber: 'border-amber-600 bg-amber-600 text-white',
    slate: 'border-slate-700 bg-slate-700 text-white',
  }[tone];
  return (
    <button
      onClick={onClick}
      title={title}
      className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition ${
        active ? activeColors : inactiveColors
      }`}
    >
      <span>{label}</span>
      <span className="tabular-nums">{count}</span>
    </button>
  );
}
